import { FC, memo, RefObject, useState, useMemo, useEffect } from 'react';

import cx from 'classnames';

import { LuChevronsUpDown } from 'react-icons/lu';
import { IoCheckmark } from 'react-icons/io5';

import { OButton, OPopper } from '@src/shared';

import { IConverterTabsScrollControls, IUnitItem, TConverterTab } from '@src/types';

import styles from '../../../../calculator.module.scss';

interface IConverterFieldProps {
  type: 'top' | 'bottom';
  value: string;
  convertedValue: string | number;
  error: string | null;
  sizeClass: string;
  fieldsSwapped: boolean;
  selectedUnit: string;
  anchorRef: RefObject<HTMLDivElement>;
  isOpen: boolean;
  onToggle: undefined | (() => void);
  onClose: () => void;
  onUnitSelect: (unit: string) => void;
  activeTab: TConverterTab;
  setActiveTab: (tab: TConverterTab) => void;
  converterTabs: TConverterTab[];
  currentData: IUnitItem[];
  tabsScroll: IConverterTabsScrollControls;
  isReadOnly: boolean;
}

const ConverterField: FC<IConverterFieldProps> = ({
  type,
  value,
  convertedValue,
  error,
  sizeClass,
  fieldsSwapped,
  selectedUnit,
  anchorRef,
  isOpen,
  onToggle,
  onClose,
  onUnitSelect,
  activeTab,
  setActiveTab,
  converterTabs,
  currentData,
  tabsScroll,
  isReadOnly = false,
}) => {
  const displayValue =
    type === 'top' ? (fieldsSwapped ? convertedValue : value) : fieldsSwapped ? value : convertedValue;

  const secondaryClass =
    type === 'top' ? fieldsSwapped && styles.displayMainSecondary : !fieldsSwapped && styles.displayMainSecondary;

  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  useEffect(() => {
    setSearchQuery('');
  }, [activeTab]);

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return currentData;
    const lowerQuery = searchQuery.toLowerCase();
    return currentData.filter(
      (item) => item.name.toLowerCase().includes(lowerQuery) || item.code.toLowerCase().includes(lowerQuery)
    );
  }, [currentData, searchQuery]);

  return (
    <>
      <div className={styles.display}>
        <div className={styles.displayMainWrapper}>
          <div className={cx(styles.displayMain, secondaryClass, sizeClass)}>{displayValue}</div>
          <span className={styles.displayUnit}>{selectedUnit}</span>
          <div ref={anchorRef} onClick={(e) => e.stopPropagation()}>
            <LuChevronsUpDown
              className={cx(styles.fieldIcon, { [styles.fieldIconReadOnly]: isReadOnly })}
              size={16}
              onClick={(e) => {
                e.stopPropagation();
                !isReadOnly && onToggle && onToggle();
              }}
            />
          </div>
        </div>
        {type === 'top' && error && <div className={styles.displayError}>{error}</div>}
      </div>
      <OPopper
        disableClose
        closeOnScroll={false}
        isOpen={isOpen}
        anchorEl={anchorRef.current}
        position="bottom-end"
        onClose={onClose}
        icon={null}
        customStyles={{
          root: { zIndex: 2000, border: 'none' },
          boxRoot: {
            backgroundColor: 'var(--calculator-dropdown-bg)',
            borderRadius: '16px',
          },
        }}
      >
        <div className={styles.converterDropdown}>
          <div
            ref={tabsScroll.tabsRef}
            className={cx(
              styles.converterDropdownTabs,
              tabsScroll.isDraggingTabs && styles.converterDropdownTabsDragging
            )}
            onPointerDown={tabsScroll.handleTabsPointerDown}
            onPointerMove={tabsScroll.handleTabsPointerMove}
            onPointerUp={tabsScroll.handleTabsPointerEnd}
            onPointerLeave={tabsScroll.handleTabsPointerEnd}
            onPointerCancel={tabsScroll.handleTabsPointerEnd}
          >
            {converterTabs.map((tab) => (
              <OButton
                key={tab}
                text={tab.charAt(0).toUpperCase() + tab.slice(1)}
                className={cx(styles.converterDropdownTab, activeTab === tab && styles.converterDropdownTabActive)}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab(tab);
                }}
              ></OButton>
            ))}
          </div>
          <input
            type="text"
            className={styles.converterDropdownSearch}
            placeholder="Search by name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          <div className={styles.converterDropdownContent}>
            {filteredData.map((item) => (
              <div
                key={item.code}
                className={cx(
                  styles.converterDropdownItem,
                  item.code === selectedUnit && styles.converterDropdownItemActive
                )}
                onClick={() => {
                  onUnitSelect(item.code);
                  onClose();
                }}
              >
                <div>
                  <div className={styles.converterDropdownName}>{item.name}</div>
                  <div className={styles.converterDropdownCode}>{item.code}</div>
                </div>
                {item.code === selectedUnit && <IoCheckmark className={styles.converterDropdownCheck} />}
              </div>
            ))}
          </div>
        </div>
      </OPopper>
    </>
  );
};

export default memo(ConverterField);
